import React, { useEffect, useState } from 'react';
import * as R from 'ramda';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { formatNumberWithoutDecimals } from 'utils/helpers/format';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import PropTypes from 'prop-types';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  addDays,
  formatISO,
  isAfter,
  isBefore,
  parseISO,
  startOfDay,
  startOfToday,
} from 'date-fns';
import { MDialog } from 'common';
import { isLessThan } from 'utils/helpers/formValidations';
import { LT_VAL_CHAR, REQUIRED } from 'utils/helpers/validationMessages';
import DataDisplayItem from '../components/DataDisplayItem';
import { isOptionEqualToValue } from '../utils';
import {
  useEditBatchMutation,
  useGet1090ProductsDesignationsQuery,
  useGetBaseProductsQuery,
} from '../batchesEndpoints';

const editContainerStyle = {
  display: 'grid',
  gap: { lg: 4, xs: 0 },
  gridTemplateColumns: { lg: '1fr 1fr' },
};

const pickerPaperStyles = {
  sx: {
    border: 1,
    borderColor: 'graphic.light',
    borderRadius: 0,
    'span.MuiTypography-root': {
      color: 'inherit',
    },
  },
};

const EditBatch = React.forwardRef((props, ref) => {
  const { batchData, batchId, batchOwner, isEditPermission, onValueChange, setAvailableDate } =
    props;

  const batchDataProp = R.prop(R.__, batchData);
  const [isFuelForExport, setIsFuelForExport] = useState(
    R.equals('Fuel For Export'.toLowerCase(), String(batchDataProp('fuelType')).toLowerCase())
  );

  const defaultState = {
    availableDate: batchDataProp('availableDate'),
    comment: R.propOr('', 'comment', batchData),
    component:
      batchDataProp('component') === null && isFuelForExport
        ? { data: 'N/A', display: 'N/A' }
        : { data: batchDataProp('component'), display: batchDataProp('component') },
    fuelType:
      R.isNil(batchDataProp('fuelType')) ||
      batchDataProp('fuelType') === 'Missing Product Designation'
        ? { data: '', display: '' }
        : { data: batchDataProp('fuelType'), display: batchDataProp('fuelType') },
    origin: {
      originId: batchDataProp('originId'),
      originName: batchDataProp('originName'),
    },
    oxygenate:
      batchDataProp('oxygenate') === null && isFuelForExport
        ? { data: 'N/A', display: 'N/A' }
        : { data: batchDataProp('oxygenate'), display: batchDataProp('oxygenate') },
    product: {
      isGasoline: batchDataProp('isGasoline'),
      productId: batchDataProp('productId'),
      productName: batchDataProp('productName'),
    },
    purchaseOrder: R.propOr('', 'purchaseOrder', batchData),
    rvp:
      batchDataProp('rvp') === null && isFuelForExport
        ? { data: 'N/A', display: 'N/A' }
        : { data: batchDataProp('rvp'), display: batchDataProp('rvp') },
    sulphurContent: {
      data: batchDataProp('sulphurContent'),
      display: batchDataProp('sulphurContent'),
    },
    volume: batchDataProp('volume'),
  };

  const [batchUpdates, setBatchUpdates] = useState(defaultState);
  const {
    availableDate,
    comment,
    component,
    fuelType,
    origin,
    oxygenate,
    product,
    purchaseOrder,
    rvp,
    sulphurContent,
  } = batchUpdates;

  const navigate = useNavigate();

  const [validationState, setValidationState] = useState({
    availableDate: '',
    comments: '',
    purchaseOrder: '',
  });

  const [isBackDialogOpen, setIsBackDialogOpen] = useState(false);

  const { data: productsQuery = [], isSuccess: isSuccessProducts } = useGetBaseProductsQuery(
    {
      params: { dataCompanyId: batchOwner.dataCompanyId },
    },
    { skip: R.isNil(batchOwner) }
  );

  const handleChange = (key, value) => setBatchUpdates(R.assoc(key, value, batchUpdates));

  const validateAvailableDate = date => {
    const parsedDate = typeof date === 'string' ? parseISO(date) : date;
    if (R.isNil(date)) {
      setValidationState(R.assoc('availableDate', 'Date required'));
    } else if (parsedDate.toString() === 'Invalid Date') {
      setValidationState(R.assoc('availableDate', 'Invalid Date'));
    } else if (isBefore(startOfDay(parsedDate), startOfToday())) {
      setValidationState(R.assoc('availableDate', 'Cannot be in the past'));
    } else if (isAfter(startOfDay(parsedDate), addDays(startOfToday(), 60))) {
      setValidationState(R.assoc('availableDate', 'Cannot be more than 60 days out'));
    } else {
      setValidationState(R.assoc('availableDate', ''));
    }
  };

  const validatePurchaseOrder = R.compose(
    setValidationState,
    R.assoc('purchaseOrder'),
    R.ifElse(R.gte(40), R.always(''), R.always('Cannot exceed 40 characters')),
    R.length
  );

  const validateCommentConditions = R.cond([
    [R.isEmpty(R.__), R.always(REQUIRED)],
    [value => !isLessThan(value, 255), R.always(LT_VAL_CHAR(255))],
    [R.T, R.always('')],
  ]);

  const validateComments = R.compose(
    setValidationState,
    R.assoc('comments'),
    validateCommentConditions
  );

  /* check data is valid prior to user interaction */
  useEffect(() => {
    validateAvailableDate(availableDate);
    validatePurchaseOrder(purchaseOrder);
    validateComments(comment);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isProductGasoline = R.propEq('isGasoline', true, product);

  const {
    data: productDesignations = {
      distillateOptions: { fuelType: [], sulphurContent: [] },
      gasolineOptions: { component: [], fuelType: [], oxygenate: [], rvp: [] },
    },
    isFetching: isFetchingDesignations,
    isSuccess: isDesignationsSuccess,
  } = useGet1090ProductsDesignationsQuery({ params: { isGasoline: isProductGasoline } });

  const isFuelTypeSummerCbobOrCg = R.anyPass([
    R.propEq('data', 'Summer CBOB'),
    R.propEq('data', 'Summer CG'),
    R.propEq('data', 'Summer RBOB'),
    R.propEq('data', 'Summer RFG'),
  ])(fuelType);

  const rvpEnabled = R.and(
    R.and(isDesignationsSuccess, isProductGasoline),
    isFuelTypeSummerCbobOrCg
  );

  const isJetFuel = R.propEq('data', 'Jet Fuel', fuelType);
  const isBlendstock = R.propEq('data', 'Gasoline Blendstock', fuelType);

  const getFilteredComponents = (components, selectedFuelType) => {
    if (R.propEq('data', 'Gasoline Blendstock', selectedFuelType)) {
      return (components || [])
        .filter((_, index) => index !== 1)
        .map(item => ({
          data: item,
          display: item,
        }));
    }
    return (components || []).map(item => ({
      data: item,
      display: item,
    }));
  };

  const handleAvailableDateChange = value => {
    validateAvailableDate(value);
    const date = R.tryCatch(R.o(formatISO, startOfDay), R.always(null))(value);
    setBatchUpdates(R.assoc('availableDate', date));
    setAvailableDate(date);
  };

  const handlePurchaseOrderChange = R.compose(
    setBatchUpdates,
    R.assoc('purchaseOrder'),
    R.tap(validatePurchaseOrder),
    R.path(['target', 'value'])
  );

  const handleCommentsChange = R.compose(
    setBatchUpdates,
    R.assoc('comment'),
    R.tap(validateComments),
    R.path(['target', 'value'])
  );

  const handleProductChange = (_, value) => {
    setBatchUpdates(
      R.mergeLeft({
        component: { data: '', display: '' },
        fuelType: { data: '', display: '' },
        oxygenate: { data: '', display: '' },
        product: value,
        rvp: { data: null, display: null },
        sulphurContent: { data: null, display: null },
      })
    );
    setIsFuelForExport(false);
  };

  const handleFuelTypeChange = (_, value) => {
    if (R.propEq('data', 'Jet Fuel', value) || R.propEq('data', 'Fuel For Export', value)) {
      setBatchUpdates(
        R.mergeLeft({
          component: isProductGasoline ? { data: 'N/A', display: 'N/A' } : null,
          fuelType: value,
          oxygenate: isProductGasoline ? { data: 'N/A', display: 'N/A' } : null,
          rvp: isProductGasoline ? { data: 'N/A', display: 'N/A' } : null,
          sulphurContent: isProductGasoline ? null : { data: 'N/A', display: 'N/A' },
        })
      );
    } else if (R.propEq('data', 'Gasoline Blendstock', value)) {
      setBatchUpdates(
        R.mergeLeft({
          component: null,
          fuelType: value,
          oxygenate: { data: 'N/A', display: 'N/A' },
          rvp: { data: 'N/A', display: 'N/A' },
          sulphurContent: isProductGasoline ? null : { data: 'N/A', display: 'N/A' },
        })
      );
    } else {
      setBatchUpdates(
        R.mergeLeft({
          component: null,
          fuelType: value,
          oxygenate: null,
          rvp: null,
          sulphurContent: null,
        })
      );
    }
    setIsFuelForExport(R.propEq('data', 'Fuel For Export', value));
  };

  const hasNoUpdates = R.all(R.equals(true), [
    R.equals(batchDataProp('availableDate'), batchUpdates?.availableDate),
    R.equals(batchDataProp('originId'), R.path(['origin', 'originId'], batchUpdates)),
    R.equals(batchDataProp('productId'), R.path(['product', 'productId'], batchUpdates)),
    R.equals(batchDataProp('fuelType') ?? '', R.path(['fuelType', 'data'], batchUpdates)),
    R.equals(batchDataProp('oxygenate'), R.path(['oxygenate', 'data'], batchUpdates)),
    R.equals(batchDataProp('rvp'), R.path(['rvp', 'data'], batchUpdates)),
    R.equals(
      R.defaultTo('', batchDataProp('component')),
      R.defaultTo('', R.path(['component', 'data'], batchUpdates))
    ),
    R.equals(batchDataProp('comment') ?? '', batchUpdates?.comment),
    R.equals(batchDataProp('purchaseOrder') ?? '', batchUpdates?.purchaseOrder),
    R.equals(
      batchDataProp('sulphurContent') ?? '',
      R.path(['sulphurContent', 'data'], batchUpdates) ?? ''
    ),
  ]);

  const isInvalid = [
    R.isNil(availableDate),
    R.isNil(origin),
    R.isNil(product),
    R.or(R.isNil(fuelType), R.isEmpty(fuelType?.data)),
    R.and(isProductGasoline, R.isNil(R.prop('data', oxygenate))),
    R.and(isProductGasoline, R.or(R.isNil(component), R.isNil(R.prop('data', component)))),
    R.allPass([
      () => !isProductGasoline,
      () => !isJetFuel,
      () => !isFuelForExport,
      () => R.isNil(sulphurContent?.data),
    ])(),
    R.and(
      isProductGasoline,
      R.and(isFuelTypeSummerCbobOrCg, R.or(R.isNil(R.prop('display', rvp)), R.isNil(rvp)))
    ),
    R.not(R.isEmpty(validationState.availableDate)),
  ].includes(true);

  const [submit] = useEditBatchMutation();
  const handleSave = () => {
    return submit({
      body: {
        availableDate,
        batchId: parseInt(batchId),
        comments: comment,
        component: component ? R.prop('data', component) : null,
        fuelType: fuelType ? fuelType?.data : null,
        originId: origin.originId,
        oxygenate: oxygenate ? R.prop('data', oxygenate) : null,
        po: purchaseOrder,
        productId: product?.productId,
        rvp: rvp?.data,
        sulphurContent: sulphurContent ? sulphurContent?.data : null,
      },
    });
  };
  useEffect(() => {
    onValueChange(isInvalid, hasNoUpdates);
  }, [isInvalid, hasNoUpdates, onValueChange]);

  React.useImperativeHandle(ref, () => ({ handleSave }));
  const { pathname } = useLocation();
  const isEditBatchMode = pathname.toLowerCase().includes('/edit');

  const hasEditPermissions = R.all(R.equals(true), [isEditBatchMode, isEditPermission]);

  return (
    <>
      <Box sx={editContainerStyle}>
        <DataDisplayItem label="Origin" value={batchDataProp('originName')} />
        <DataDisplayItem
          icon={<InfoRoundedIcon fontSize="small" sx={{ color: 'graphic.main' }} />}
          label="Volume"
          sx={{ gridColumn: { lg: '2/3' }, gridRow: { lg: '1/2', marginTop: 10 } }}
          tooltip="The Batch volume is calculated using the total sum of the shipments in this Batch."
          value={`${formatNumberWithoutDecimals(R.defaultTo(0, batchDataProp('volume')))} bbl`}
        />
        <DatePicker
          disabled={!hasEditPermissions}
          label="Available Date"
          maxDate={addDays(startOfToday(), 60)}
          minDate={startOfToday()}
          onChange={handleAvailableDateChange}
          slotProps={{
            desktopPaper: pickerPaperStyles,
            textField: {
              error: !R.isEmpty(validationState.availableDate) && hasEditPermissions,
              helperText: hasEditPermissions && validationState.availableDate,
              sx: { width: { lg: 342 } },
            },
          }}
          value={parseISO(availableDate)}
        />
        <Autocomplete
          disableClearable
          disabled={!isSuccessProducts || !hasEditPermissions}
          getOptionLabel={R.prop('productName')}
          isOptionEqualToValue={R.eqProps('productId')}
          onChange={handleProductChange}
          options={productsQuery}
          renderInput={params => (
            <TextField {...params} label="Product" sx={{ width: { lg: 342 } }} />
          )}
          sx={{ gridColumn: { lg: '2/3' }, gridRow: { lg: '2/3' } }}
          value={product}
        />
        <TextField
          disabled={!hasEditPermissions}
          label="Purchase Order"
          onChange={handlePurchaseOrderChange}
          sx={{ width: { lg: 342 } }}
          value={purchaseOrder}
        />
        <Autocomplete
          disableClearable
          disabled={!isDesignationsSuccess || !hasEditPermissions}
          getOptionLabel={R.prop('display')}
          isOptionEqualToValue={R.eqProps('data')}
          onChange={handleFuelTypeChange}
          options={
            isProductGasoline
              ? (productDesignations?.gasolineOptions?.fuelType || []).map(item => ({
                  data: item,
                  display: item,
                }))
              : (productDesignations?.distillateOptions?.fuelType || []).map(item => ({
                  data: item,
                  display: item,
                }))
          }
          renderInput={params => (
            <TextField
              {...params}
              error={
                hasEditPermissions &&
                (R.isNil(fuelType) || R.isNil(fuelType.data) || fuelType.data === '')
              }
              helperText={
                hasEditPermissions &&
                (R.isNil(fuelType) || R.isNil(fuelType.data) || fuelType.data === '')
                  ? 'Required'
                  : ''
              }
              label="Fuel Type"
              required={hasEditPermissions}
              sx={{ width: { lg: 342 } }}
            />
          )}
          value={fuelType?.data ? fuelType : { data: '', display: '' }}
        />
        <TextField
          disabled={!hasEditPermissions}
          label="Comments"
          multiline
          onChange={handleCommentsChange}
          rows={4}
          sx={{
            gridColumn: { lg: '1/2' },
            gridRowEnd: { lg: '5/5' },
            gridRowStart: { lg: '4/5' },
            width: { lg: 342 },
          }}
          value={comment}
        />
        <Stack
          direction="column"
          justifyContent="right"
          spacing={1}
          sx={{ gap: 4, gridColumn: { lg: '2/2' }, gridRow: { lg: '4/5' }, mb: 1, mt: 1 }}
        >
          <Autocomplete
            disableClearable
            disabled={!hasEditPermissions || isJetFuel || isFuelForExport}
            getOptionLabel={R.prop('display')}
            hidden={isProductGasoline}
            isOptionEqualToValue={isOptionEqualToValue('data')}
            loading={isFetchingDesignations}
            onChange={(_, value) => handleChange('sulphurContent', value)}
            options={
              !isProductGasoline
                ? (productDesignations?.distillateOptions?.sulphurContent || []).map(item => ({
                    data: item,
                    display: item,
                  }))
                : []
            }
            renderInput={inputParams => (
              <TextField
                {...inputParams}
                error={
                  hasEditPermissions &&
                  !isProductGasoline &&
                  R.or(R.isNil(sulphurContent?.data), R.isEmpty(R.prop('data', sulphurContent)))
                }
                helperText={
                  hasEditPermissions &&
                  !isProductGasoline &&
                  R.or(R.isNil(sulphurContent?.data), R.isEmpty(R.prop('data', sulphurContent)))
                    ? 'Required'
                    : ''
                }
                label="Sulfur Content"
                required={!isProductGasoline && hasEditPermissions}
                sx={{ width: { lg: 342 } }}
              />
            )}
            value={sulphurContent?.data ? sulphurContent : { data: '', display: '' }}
          />

          <Autocomplete
            disableClearable
            disabled={!hasEditPermissions || !isProductGasoline || isBlendstock || isFuelForExport}
            getOptionLabel={R.prop('display')}
            hidden={!isProductGasoline}
            isOptionEqualToValue={isOptionEqualToValue('data')}
            onChange={(_, value) => handleChange('oxygenate', value)}
            options={
              isProductGasoline
                ? (productDesignations?.gasolineOptions?.oxygenate || []).map(item => ({
                    data: item,
                    display: item,
                  }))
                : []
            }
            renderInput={params => (
              <TextField
                {...params}
                error={
                  hasEditPermissions &&
                  isProductGasoline &&
                  R.or(
                    R.isNil(oxygenate),
                    R.or(R.isEmpty(R.prop('data', oxygenate)), R.isNil(R.prop('data', oxygenate)))
                  )
                }
                helperText={
                  hasEditPermissions &&
                  isProductGasoline &&
                  R.or(
                    R.isNil(oxygenate),
                    R.or(R.isEmpty(R.prop('data', oxygenate)), R.isNil(R.prop('data', oxygenate)))
                  )
                    ? 'Required'
                    : ''
                }
                label="Oxygenate"
                required={
                  hasEditPermissions && isProductGasoline && !isBlendstock && !isFuelForExport
                }
                sx={{ width: { lg: 342 } }}
              />
            )}
            sx={{ width: { lg: 342 } }}
            value={oxygenate?.data ? oxygenate : { data: '', display: '' }}
          />
          <Autocomplete
            disableClearable
            disabled={!rvpEnabled || !hasEditPermissions}
            getOptionLabel={R.prop('display')}
            hidden={!isProductGasoline}
            isOptionEqualToValue={R.eqProps('data')}
            onChange={(_, value) => handleChange('rvp', value)}
            options={
              isProductGasoline
                ? (productDesignations?.gasolineOptions?.rvp || []).map(item => ({
                    data: item,
                    display: item,
                  }))
                : []
            }
            renderInput={params => (
              <TextField
                {...params}
                error={
                  hasEditPermissions &&
                  isProductGasoline &&
                  isFuelTypeSummerCbobOrCg &&
                  R.or(R.isNil(rvp?.data), R.isEmpty(R.prop('data', rvp)))
                }
                helperText={
                  hasEditPermissions &&
                  isProductGasoline &&
                  isFuelTypeSummerCbobOrCg &&
                  R.or(R.isNil(rvp?.data), R.isEmpty(R.prop('data', rvp)))
                    ? 'Required'
                    : ''
                }
                label="RVP"
                required={hasEditPermissions && isProductGasoline && isFuelTypeSummerCbobOrCg}
                sx={{ width: { lg: 342 } }}
              />
            )}
            value={rvp?.data ? rvp : { data: '', display: '' }}
          />
          <Autocomplete
            disableClearable
            disabled={isFuelForExport || !hasEditPermissions}
            getOptionLabel={R.prop('display')}
            hidden={!isProductGasoline}
            isOptionEqualToValue={isOptionEqualToValue('data')}
            loading={isFetchingDesignations}
            onChange={(_, value) => handleChange('component', value)}
            options={
              isProductGasoline
                ? getFilteredComponents(productDesignations?.gasolineOptions?.component, fuelType)
                : []
            }
            renderInput={inputParams => (
              <TextField
                {...inputParams}
                error={
                  hasEditPermissions &&
                  isProductGasoline &&
                  !isFuelForExport &&
                  R.or(
                    R.isNil(component),
                    R.or(R.isEmpty(R.prop('data', component)), R.isNil(R.prop('data', component)))
                  )
                }
                helperText={
                  hasEditPermissions &&
                  isProductGasoline &&
                  !isFuelForExport &&
                  R.or(
                    R.isNil(component),
                    R.or(R.isEmpty(R.prop('data', component)), R.isNil(R.prop('data', component)))
                  )
                    ? 'Required'
                    : ''
                }
                label="Component"
                required={isProductGasoline && hasEditPermissions}
                sx={{ width: { lg: 342 } }}
              />
            )}
            value={component?.data ? component : { data: '', display: '' }}
          />
        </Stack>
      </Box>
      <MDialog
        classes={{ paperScrollPaper: { maxHeight: '95%' } }}
        header="Unsaved Changes"
        leftButtonAction={() => {
          setIsBackDialogOpen(false);
          navigate('../batches/batch');
        }}
        leftButtonText="Continue"
        onClose={() => {
          setIsBackDialogOpen(false);
        }}
        open={isBackDialogOpen}
        rightButtonAction={() => setIsBackDialogOpen(false)}
        rightButtonText="Keep Editing"
      >
        <Typography>Are you sure you want to leave this page? You have unsaved changes.</Typography>
      </MDialog>
    </>
  );
});

EditBatch.propTypes = {
  batchData: PropTypes.shape({
    availableDate: PropTypes.string,
    comment: PropTypes.string,
    fuelType: PropTypes.string,
    origin: PropTypes.shape({
      originId: PropTypes.number,
    }),
    oxygenate: PropTypes.string,
    product: PropTypes.shape({
      productId: PropTypes.number,
    }),
    purchaseOrder: PropTypes.string,
    rvp: PropTypes.string,
  }).isRequired,
  batchId: PropTypes.string.isRequired,
  batchOwner: PropTypes.shape({
    batchOwnerName: PropTypes.string,
    dataCompanyId: PropTypes.number,
  }).isRequired,
  isEditPermission: PropTypes.bool.isRequired,
  onValueChange: PropTypes.func.isRequired,
  productId: PropTypes.number.isRequired,
  setAvailableDate: PropTypes.func.isRequired,
};

export default EditBatch;
